import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Input, TreeSelect } from "antd";
import { useAppSelector } from "../../../redux/hooks";
import { notifyError, notifyWarning } from "../../../utils/notification";
import {
  getHierarchyModulesPage,
  getHierarchyProjectsPage,
  getHierarchyRequirementsPage,
  getHierarchyScriptsPage,
  HierarchyModuleNode,
  HierarchyProjectNode,
  HierarchyRequirementNode,
  HierarchyScriptNode,
} from "../action";

type NodeType = "project" | "module" | "requirement" | "script" | "load-more";

interface ScriptPayload {
  projectId: string;
  moduleId: string;
  requirementId: string;
  testcaseId: string;
}

interface SelectorTreeNode {
  key: string;
  value: string;
  title: React.ReactNode;
  children?: SelectorTreeNode[];
  childrenLoaded?: boolean;
  isLeaf?: boolean;
  selectable?: boolean;
  disableCheckbox?: boolean;
  checkable?: boolean;
  nodeType: NodeType;
  label: string;
  projectId?: string;
  moduleId?: string;
  requirementId?: string;
}

interface LoadMoreParams {
  level: "project" | "module" | "requirement" | "script";
  cursor: string;
  parentKey?: string;
  projectId?: string;
  moduleId?: string;
  requirementId?: string;
}

export interface OnSubmitParams {
  accountId: string;
  suiteName?: string;
  testcaseIds: string[];
  testcaseObjects: ScriptPayload[];
  closeModal: () => void;
  refresh: () => Promise<any>;
}

interface TestSuiteScriptSelectorModalProps {
  open: boolean;
  close: (open: boolean) => void;
  handleRefresh: () => Promise<any>;
  title: string;
  submitButtonText: string;
  showSuiteNameField?: boolean;
  onSubmit: (params: OnSubmitParams) => Promise<void>;
}

const removeLoadMoreNodes = (nodes: SelectorTreeNode[] = []) =>
  nodes.filter((node) => node.nodeType !== "load-more");

const toScriptPayload = (rawValue: string): ScriptPayload | undefined => {
  try {
    const parsedValue = JSON.parse(rawValue);
    if (
      parsedValue?.projectId &&
      parsedValue?.moduleId &&
      parsedValue?.requirementId &&
      parsedValue?.testcaseId
    ) {
      return parsedValue;
    }
  } catch (error) {
    return undefined;
  }
  return undefined;
};

const toScriptValue = ({
  projectId,
  moduleId,
  requirementId,
  testcaseId,
}: ScriptPayload) =>
  JSON.stringify({ projectId, moduleId, requirementId, testcaseId });

const mergeUniqueNodes = (
  existingNodes: SelectorTreeNode[] = [],
  incomingNodes: SelectorTreeNode[] = [],
) => {
  const uniqueNodes = new Map<string, SelectorTreeNode>();

  [...existingNodes, ...incomingNodes].forEach((node) => {
    if (!uniqueNodes.has(node.key)) {
      uniqueNodes.set(node.key, node);
      return;
    }

    const existingNode = uniqueNodes.get(node.key) as SelectorTreeNode;
    uniqueNodes.set(node.key, {
      ...existingNode,
      ...node,
      children: node.children ?? existingNode.children,
      childrenLoaded: existingNode.childrenLoaded || node.childrenLoaded,
    });
  });

  return Array.from(uniqueNodes.values());
};

const updateNodeChildren = (
  nodes: SelectorTreeNode[],
  targetKey: string,
  updater: (node: SelectorTreeNode) => SelectorTreeNode,
): SelectorTreeNode[] =>
  nodes.map((node) => {
    if (node.key === targetKey) {
      return updater(node);
    }

    if (node.children?.length) {
      return {
        ...node,
        children: updateNodeChildren(node.children, targetKey, updater),
      };
    }

    return node;
  });

const findNodeByKey = (
  nodes: SelectorTreeNode[],
  targetKey: string,
): SelectorTreeNode | undefined => {
  for (const node of nodes) {
    if (node.key === targetKey) {
      return node;
    }

    if (node.children?.length) {
      const foundNode = findNodeByKey(node.children, targetKey);
      if (foundNode) return foundNode;
    }
  }

  return undefined;
};

const TestSuiteScriptSelectorModal = ({
  open,
  close,
  handleRefresh,
  title,
  submitButtonText,
  showSuiteNameField = false,
  onSubmit,
}: TestSuiteScriptSelectorModalProps) => {
  const [treeData, setTreeData] = useState<SelectorTreeNode[]>([]);
  const [selectedScriptValues, setSelectedScriptValues] = useState<string[]>(
    [],
  );
  const [isTreeLoading, setTreeLoading] = useState<boolean>(false);
  const [loadingNodeKeys, setLoadingNodeKeys] = useState<string[]>([]);

  const [form] = Form.useForm();
  const accountId = useAppSelector(
    ({ userData }: any) => userData?.selectedAccount?.accountId,
  );

  const setNodeLoading = (nodeKey: string, loading: boolean) => {
    setLoadingNodeKeys((prev) => {
      if (loading && !prev.includes(nodeKey)) return [...prev, nodeKey];
      if (!loading) return prev.filter((key) => key !== nodeKey);
      return prev;
    });
  };

  const buildProjectNodes = (projects: HierarchyProjectNode[]) =>
    projects.map((project) => ({
      key: `project:${project.projectId}`,
      value: `project:${project.projectId}`,
      title: project.name,
      label: project.name,
      nodeType: "project" as NodeType,
      projectId: project.projectId,
      disableCheckbox: true,
      selectable: false,
      checkable: false,
      isLeaf: false,
      childrenLoaded: false,
    }));

  const buildModuleNodes = (
    modules: HierarchyModuleNode[],
    projectId: string,
  ) =>
    modules.map((module) => ({
      key: `module:${projectId}:${module.moduleId}`,
      value: `module:${projectId}:${module.moduleId}`,
      title: module.name,
      label: module.name,
      nodeType: "module" as NodeType,
      projectId,
      moduleId: module.moduleId,
      disableCheckbox: true,
      selectable: false,
      checkable: false,
      isLeaf: false,
      childrenLoaded: false,
    }));

  const buildRequirementNodes = (
    requirements: HierarchyRequirementNode[],
    projectId: string,
    moduleId: string,
  ) =>
    requirements.map((requirement) => ({
      key: `requirement:${projectId}:${moduleId}:${requirement.requirementId}`,
      value: `requirement:${projectId}:${moduleId}:${requirement.requirementId}`,
      title: requirement.requirementDescription || requirement.requirementId,
      label: requirement.requirementDescription || requirement.requirementId,
      nodeType: "requirement" as NodeType,
      projectId,
      moduleId,
      requirementId: requirement.requirementId,
      disableCheckbox: true,
      selectable: false,
      checkable: false,
      isLeaf: false,
      childrenLoaded: false,
    }));

  const buildScriptNodes = (scripts: HierarchyScriptNode[]) =>
    scripts.map((script) => {
      const scriptPayload = {
        projectId: script.projectId,
        moduleId: script.moduleId,
        requirementId: script.requirementId,
        testcaseId: script.testcaseId,
      };

      return {
        key: `script:${script.projectId}:${script.moduleId}:${script.requirementId}:${script.testcaseId}`,
        value: toScriptValue(scriptPayload),
        title: script.scenario || script.testcaseId,
        label: script.scenario || script.testcaseId,
        nodeType: "script" as NodeType,
        projectId: script.projectId,
        moduleId: script.moduleId,
        requirementId: script.requirementId,
        isLeaf: true,
        checkable: true,
      };
    });

  const buildLoadMoreNode = ({
    level,
    cursor,
    parentKey,
    projectId,
    moduleId,
    requirementId,
  }: LoadMoreParams): SelectorTreeNode => {
    const loadMoreNodeKey = `load-more:${level}:${parentKey || "root"}:${cursor}`;
    const loading = loadingNodeKeys.includes(loadMoreNodeKey);

    return {
      key: loadMoreNodeKey,
      value: loadMoreNodeKey,
      label: "Load more",
      title: (
        <Button
          type="link"
          size="small"
          className="fp_tree_load_more_btn"
          loading={loading}
          onMouseDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.stopPropagation();
            handleLoadMore({
              level,
              cursor,
              parentKey,
              projectId,
              moduleId,
              requirementId,
            });
          }}
        >
          Load more
        </Button>
      ),
      nodeType: "load-more",
      disableCheckbox: true,
      selectable: false,
      checkable: false,
      isLeaf: true,
      projectId,
      moduleId,
      requirementId,
    };
  };

  const setRootProjects = (
    projects: HierarchyProjectNode[],
    hasMore: boolean,
    nextCursor?: string,
    append: boolean = false,
  ) => {
    setTreeData((prev) => {
      const currentTree = append ? removeLoadMoreNodes(prev) : [];
      let nextTree = mergeUniqueNodes(currentTree, buildProjectNodes(projects));

      if (hasMore && nextCursor) {
        nextTree = [
          ...nextTree,
          buildLoadMoreNode({
            level: "project",
            cursor: nextCursor,
          }),
        ];
      }

      return nextTree;
    });
  };

  const setChildrenForNode = (
    parentKey: string,
    children: SelectorTreeNode[],
    hasMore: boolean,
    nextCursor: string | undefined,
    level: "module" | "requirement" | "script",
    append: boolean,
    ids: { projectId?: string; moduleId?: string; requirementId?: string },
  ) => {
    setTreeData((prev) =>
      updateNodeChildren(prev, parentKey, (node) => {
        const currentChildren = append
          ? removeLoadMoreNodes(node.children || [])
          : [];
        let nextChildren = mergeUniqueNodes(currentChildren, children);

        if (hasMore && nextCursor) {
          nextChildren = [
            ...nextChildren,
            buildLoadMoreNode({
              level,
              cursor: nextCursor,
              parentKey,
              ...ids,
            }),
          ];
        }

        return {
          ...node,
          children: nextChildren,
          childrenLoaded: true,
        };
      }),
    );
  };

  const fetchProjects = async (cursor?: string, append: boolean = false) => {
    if (!accountId) return;

    if (!append) {
      setTreeLoading(true);
    }

    try {
      const { items, hasMore, nextCursor } = await getHierarchyProjectsPage(
        accountId,
        cursor,
      );
      setRootProjects(items, hasMore, nextCursor, append);
    } catch (error) {
      if (append) {
        notifyWarning("Failed to fetch additional projects");
      } else {
        notifyError("Failed to fetch projects");
      }
    } finally {
      setTreeLoading(false);
    }
  };

  const fetchModules = async (
    projectId: string,
    parentKey: string,
    cursor?: string,
    append: boolean = false,
  ) => {
    const nodeLoadingKey = cursor
      ? `load-more:module:${parentKey}:${cursor}`
      : parentKey;
    setNodeLoading(nodeLoadingKey, true);
    try {
      const { items, hasMore, nextCursor } = await getHierarchyModulesPage(
        projectId,
        cursor,
      );
      const moduleNodes = buildModuleNodes(items, projectId);
      setChildrenForNode(
        parentKey,
        moduleNodes,
        hasMore,
        nextCursor,
        "module",
        append,
        { projectId },
      );
    } catch (error) {
      notifyError("Failed to fetch modules");
    } finally {
      setNodeLoading(nodeLoadingKey, false);
    }
  };

  const fetchRequirements = async (
    projectId: string,
    moduleId: string,
    parentKey: string,
    cursor?: string,
    append: boolean = false,
  ) => {
    const nodeLoadingKey = cursor
      ? `load-more:requirement:${parentKey}:${cursor}`
      : parentKey;
    setNodeLoading(nodeLoadingKey, true);
    try {
      const { items, hasMore, nextCursor } = await getHierarchyRequirementsPage(
        moduleId,
        cursor,
      );
      const requirementNodes = buildRequirementNodes(
        items,
        projectId,
        moduleId,
      );

      setChildrenForNode(
        parentKey,
        requirementNodes,
        hasMore,
        nextCursor,
        "requirement",
        append,
        { projectId, moduleId },
      );
    } catch (error) {
      notifyError("Failed to fetch requirements");
    } finally {
      setNodeLoading(nodeLoadingKey, false);
    }
  };

  const fetchScripts = async (
    projectId: string,
    moduleId: string,
    requirementId: string,
    parentKey: string,
    cursor?: string,
    append: boolean = false,
  ) => {
    const nodeLoadingKey = cursor
      ? `load-more:script:${parentKey}:${cursor}`
      : parentKey;
    setNodeLoading(nodeLoadingKey, true);
    try {
      const { items, hasMore, nextCursor } = await getHierarchyScriptsPage(
        projectId,
        moduleId,
        requirementId,
        cursor,
      );
      const scriptNodes = buildScriptNodes(items);
      setChildrenForNode(
        parentKey,
        scriptNodes,
        hasMore,
        nextCursor,
        "script",
        append,
        { projectId, moduleId, requirementId },
      );
    } catch (error) {
      notifyError("Failed to fetch scripts");
    } finally {
      setNodeLoading(nodeLoadingKey, false);
    }
  };

  async function handleLoadMore({
    level,
    cursor,
    parentKey,
    projectId,
    moduleId,
    requirementId,
  }: LoadMoreParams) {
    if (!cursor) return;

    if (level === "project") {
      await fetchProjects(cursor, true);
      return;
    }

    if (level === "module" && parentKey && projectId) {
      await fetchModules(projectId, parentKey, cursor, true);
      return;
    }

    if (level === "requirement" && parentKey && projectId && moduleId) {
      await fetchRequirements(projectId, moduleId, parentKey, cursor, true);
      return;
    }

    if (
      level === "script" &&
      parentKey &&
      projectId &&
      moduleId &&
      requirementId
    ) {
      await fetchScripts(
        projectId,
        moduleId,
        requirementId,
        parentKey,
        cursor,
        true,
      );
    }
  }

  const handleLoadData = async (treeNode: any) => {
    const nodeKey = String(treeNode?.key || "");
    const node = findNodeByKey(treeData, nodeKey);

    if (!node || node.childrenLoaded) {
      return;
    }

    if (node.nodeType === "project" && node.projectId) {
      await fetchModules(node.projectId, node.key);
      return;
    }

    if (node.nodeType === "module" && node.projectId && node.moduleId) {
      await fetchRequirements(node.projectId, node.moduleId, node.key);
      return;
    }

    if (
      node.nodeType === "requirement" &&
      node.projectId &&
      node.moduleId &&
      node.requirementId
    ) {
      await fetchScripts(
        node.projectId,
        node.moduleId,
        node.requirementId,
        node.key,
      );
    }
  };

  const handleCancel = () => {
    close(false);
    form.resetFields();
    setTreeData([]);
    setSelectedScriptValues([]);
    setTreeLoading(false);
    setLoadingNodeKeys([]);
  };

  const handleConfirm = async () => {
    const { name } = form.getFieldsValue();
    const suiteName = typeof name === "string" ? name.trim() : "";

    if (!accountId) {
      notifyError("Account is required");
      return;
    }

    if (showSuiteNameField && !suiteName) {
      notifyError("Test suit name is required");
      return;
    }

    const selectedTestCases = selectedScriptValues
      .map((value) => toScriptPayload(value))
      .filter(Boolean) as ScriptPayload[];

    if (!selectedTestCases.length) {
      notifyWarning("Please select at least one script");
      return;
    }

    const testcaseIds = Array.from(
      new Set(selectedTestCases.map((testcase) => testcase.testcaseId)),
    );

    await onSubmit({
      accountId,
      suiteName: suiteName || undefined,
      testcaseIds,
      testcaseObjects: selectedTestCases,
      closeModal: handleCancel,
      refresh: handleRefresh,
    });
  };

  useEffect(() => {
    if (open && accountId) {
      fetchProjects();
    }
  }, [open, accountId]);

  return (
    <Modal
      className="fp_test_execution_modal"
      closable
      maskClosable={false}
      open={open}
      footer={[
        <Button
          key="discard"
          onClick={handleCancel}
          data-testid="fp_new_projec_cancel_btn"
          className="fp_btn fp_btn-outline"
        >
          Discard
        </Button>,
        <Button
          key="confirm"
          onClick={handleConfirm}
          data-testid="fp_new_project_add_btn"
          className="fp_btn fp_btn-primary"
        >
          {submitButtonText}
        </Button>,
      ]}
      onCancel={handleCancel}
    >
      <div>
        <div className="fp_test_execution_modal_header">
          <p>{title}</p>
        </div>
        <Form layout="vertical" form={form}>
          {showSuiteNameField && (
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Name is required!" }]}
            >
              <Input
                placeholder="Enter regression suite name"
                className="fp_input"
              />
            </Form.Item>
          )}
          <Form.Item label="Select test scripts">
            <TreeSelect
              style={{ width: "100%" }}
              popupClassName="fp_test_execution_modal_treeselect"
              dropdownStyle={{
                maxHeight: 400,
                overflow: "auto",
                background: "#4E4E4E",
                borderRadius: "20px",
              }}
              placeholder={
                isTreeLoading ? "Loading projects..." : "Please select"
              }
              treeCheckable
              multiple
              showSearch={false}
              allowClear
              value={selectedScriptValues}
              onChange={(values) => {
                const selectedValues = (Array.isArray(values) ? values : [])
                  .map((value) => String(value))
                  .filter((value) => Boolean(toScriptPayload(value)));
                setSelectedScriptValues(selectedValues);
              }}
              treeData={treeData}
              loadData={handleLoadData}
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default TestSuiteScriptSelectorModal;
